import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { MutationOptions } from '@/app/lib/tanstack-query';
import {
  acceptInvite,
  createUser,
  forgotPassword,
  resendEmailVerification,
  resetPassword,
  verifyEmail
} from '@/app/network/firefly-api';

type AcceptInviteParams = {
  email: string;
  inviteCode: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
};

export function useAcceptInvite(options?: Omit<UseMutationOptions<void, Error, AcceptInviteParams>, 'mutationFn'>) {
  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: data => acceptInvite(
      data.email,
      data.inviteCode,
      data.firstName,
      data.lastName,
      data.password
    ),
    onSuccess: (data, ...rest) => {
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

type CreateUserParams = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export function useCreateUser(options?: Omit<UseMutationOptions<void, Error, CreateUserParams>, 'mutationFn'>) {
  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: data => createUser(
      data.firstName,
      data.lastName,
      data.email,
      data.password
    ),
    onSuccess: (data, ...rest) => {
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

export function useForgotPassword(options?: Omit<UseMutationOptions<void, Error, { email: string }>, 'mutationFn'>) {
  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: data => forgotPassword(data.email),
    onSuccess: (data, ...rest) => {
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

export function useResetPassword(options?: Omit<UseMutationOptions<void, Error, { token: string; password: string; confirmPassword: string }>, 'mutationFn'>) {
  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: data => resetPassword(data.token, data.password, data.confirmPassword),
    onSuccess: (data, ...rest) => {
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

export function useVerifyEmail(options?: Omit<UseMutationOptions<void, Error, { token: string }>, 'mutationFn'>) {
  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: data => verifyEmail(data.token),
    onSuccess: (data, ...rest) => {
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

export function useResendVerificationEmail(options?: MutationOptions<void, Error, { email: string }>) {
  return useMutation({
    mutationFn: data => resendEmailVerification(data.email),
    ...options
  });
}
